/* Enterprise Security Suite panels — Assurance Center, Workspaces/Due Diligence Rooms,
   Enterprise Document Portal, AI Questionnaire Assistant. */
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Loader2, ShieldCheck, Briefcase, FileStack, Sparkles, Copy, Upload } from "lucide-react";

const inputCls = "rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cobalt/30";
const btnNavy = "px-4 py-2 rounded-full bg-navy text-white text-xs font-bold";
const err = (e) => formatApiErrorDetail(e.response?.data?.detail?.message || e.response?.data?.detail) || "Failed";
const Spinner = () => <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div>;
const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied to clipboard"); };

export const AssurancePanel = () => {
  const [d, setD] = useState(null);
  const [cert, setCert] = useState({ name: "", issuer: "", status: "planned", visible_in_room: false });
  const load = useCallback(() => api.get("/suite/assurance").then(({ data }) => setD(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Spinner />;
  const addCert = async () => {
    if (!cert.name) { toast.error("Name required"); return; }
    try { await api.post("/suite/certifications", cert); toast.success("Certification registry updated (audited)"); setCert({ name: "", issuer: "", status: "planned", visible_in_room: false }); load(); }
    catch (e) { toast.error(err(e)); }
  };
  return (
    <div className="space-y-4" data-testid="suite-assurance">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {[["Health", `${d.health.overall_pct}%`], ["SOC 2", `${d.readiness.soc2_pct}%`], ["HIPAA", `${d.readiness.hipaa_pct}%`],
          ["State Privacy", `${d.readiness.state_privacy_pct}%`], ["Audit Ready", `${d.readiness.audit_readiness_pct}%`],
          ["Workspaces", d.engagement.active_workspaces], ["Open DD Questions", d.engagement.open_dd_questions]].map(([l, v]) => (
          <div key={l} className="rounded-2xl bg-white border border-slate-100 p-4" data-testid={`assurance-${l.toLowerCase().replace(/ /g, "-")}`}>
            <div className="text-[10px] font-bold uppercase text-slate-400">{l}</div>
            <div className="font-heading text-xl font-extrabold text-navy mt-1">{v}</div>
          </div>
        ))}
      </div>
      <div className="rounded-3xl bg-white border border-slate-100 p-5" data-testid="assurance-certs">
        <h3 className="font-heading font-bold text-navy text-sm flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-cobalt" /> Certifications &amp; attestations registry (honest — 'issued' requires documented issuer)</h3>
        <div className="mt-3 flex flex-wrap gap-2">
          <input placeholder="Name (e.g. SOC 2 Type II)" value={cert.name} onChange={(e) => setCert({ ...cert, name: e.target.value })} data-testid="cert-name" className={inputCls} />
          <input placeholder="Issuer / audit firm" value={cert.issuer} onChange={(e) => setCert({ ...cert, issuer: e.target.value })} data-testid="cert-issuer" className={inputCls} />
          <select value={cert.status} onChange={(e) => setCert({ ...cert, status: e.target.value })} data-testid="cert-status" className={inputCls}>
            {["planned", "in_progress", "readiness_assessment", "issued", "expired", "not_pursued"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <label className="flex items-center gap-1.5 text-xs text-slate-500"><input type="checkbox" checked={cert.visible_in_room} onChange={(e) => setCert({ ...cert, visible_in_room: e.target.checked })} data-testid="cert-visible" /> visible to customers</label>
          <button onClick={addCert} data-testid="cert-add" className={btnNavy}>Add / update</button>
        </div>
        <div className="mt-3 space-y-1.5">
          {d.certifications.map((c) => (
            <div key={c.cert_id} className="flex justify-between rounded-xl bg-slate-50 px-3 py-2 text-xs" data-testid={`cert-row-${c.cert_id}`}>
              <span className="text-slate-600"><b className="text-navy">{c.name}</b>{c.issuer ? ` · ${c.issuer}` : ""}</span>
              <span className="text-slate-400">{c.status}{c.visible_in_room ? " · customer-visible" : ""}</span>
            </div>
          ))}
          {!d.certifications.length && <p className="text-[11px] text-slate-300">No certifications recorded — the registry stays empty until real engagements exist.</p>}
        </div>
      </div>
      <p className="text-[10px] text-slate-400">{d.note}</p>
    </div>
  );
};

export const WorkspacesPanel = () => {
  const [rows, setRows] = useState(null);
  const [docs, setDocs] = useState([]);
  const [detail, setDetail] = useState(null);
  const [ws, setWs] = useState({ name: "", type: "law_firm" });
  const [mbr, setMbr] = useState({ name: "", email: "", role: "reviewer" });
  const [shareDoc, setShareDoc] = useState("");
  const load = useCallback(() => {
    api.get("/suite/workspaces").then(({ data }) => setRows(data)).catch(() => {});
    api.get("/enterprise-docs").then(({ data }) => setDocs(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!rows) return <Spinner />;
  const openDetail = (id) => api.get(`/suite/workspaces/${id}`).then(({ data }) => setDetail(data)).catch((e) => toast.error(err(e)));
  const create = async () => {
    if (!ws.name) { toast.error("Organization name required"); return; }
    try { await api.post("/suite/workspaces", ws); toast.success("Workspace created"); setWs({ name: "", type: "law_firm" }); load(); }
    catch (e) { toast.error(err(e)); }
  };
  const addMember = async (wid) => {
    if (!mbr.name || !mbr.email) { toast.error("Name + email required"); return; }
    try {
      const { data } = await api.post(`/suite/workspaces/${wid}/members`, mbr);
      copy(`${window.location.origin}${data.portal_path}`);
      toast.success("Member added — portal link copied"); openDetail(wid); load();
    } catch (e) { toast.error(err(e)); }
  };
  const share = async (wid) => {
    if (!shareDoc) { toast.error("Select a document"); return; }
    try { await api.post(`/suite/workspaces/${wid}/share-doc`, { doc_id: shareDoc }); toast.success("Document shared to room (audited)"); openDetail(wid); }
    catch (e) { toast.error(err(e)); }
  };
  const answer = async (qid, useAi) => {
    try {
      if (useAi) {
        const { data } = await api.post(`/suite/questions/${qid}/answer`, { use_ai_draft: true });
        const final = window.prompt("AI draft (edit before sending):", data.ai_draft);
        if (final) { await api.post(`/suite/questions/${qid}/answer`, { answer: final }); toast.success("Answer sent"); }
      } else {
        const a = window.prompt("Answer:");
        if (a) { await api.post(`/suite/questions/${qid}/answer`, { answer: a }); toast.success("Answer sent"); }
      }
      openDetail(detail.workspace.workspace_id);
    } catch (e) { toast.error(err(e)); }
  };
  return (
    <div className="space-y-4" data-testid="suite-workspaces">
      <div className="rounded-3xl bg-white border border-slate-100 p-5">
        <h3 className="font-heading font-bold text-navy text-sm flex items-center gap-2"><Briefcase className="w-4 h-4 text-cobalt" /> Customer workspaces — Due Diligence Rooms</h3>
        <div className="mt-3 flex flex-wrap gap-2">
          <input placeholder="Organization (e.g. BigLaw LLP)" value={ws.name} onChange={(e) => setWs({ ...ws, name: e.target.value })} data-testid="ws-name" className={inputCls} />
          <select value={ws.type} onChange={(e) => setWs({ ...ws, type: e.target.value })} data-testid="ws-type" className={inputCls}>
            {["law_firm", "hospital", "insurance_carrier", "government", "enterprise", "other"].map((t) => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
          </select>
          <button onClick={create} data-testid="ws-create" className={btnNavy}>Create workspace</button>
        </div>
        <div className="mt-3 space-y-1.5">
          {rows.map((w) => (
            <button key={w.workspace_id} onClick={() => openDetail(w.workspace_id)} data-testid={`ws-row-${w.workspace_id}`}
              className="w-full text-left flex justify-between rounded-xl border border-slate-100 px-3 py-2 text-xs hover:border-cobalt transition-colors">
              <span className="text-slate-600"><b className="text-navy">{w.name}</b> · {w.type.replace("_", " ")}</span>
              <span className="text-slate-400">{(w.members || []).length} member(s) · {(w.doc_ids || []).length} doc(s)</span>
            </button>
          ))}
          {!rows.length && <p className="text-[11px] text-slate-300">No workspaces yet — create one per enterprise prospect.</p>}
        </div>
      </div>
      {detail && (
        <div className="rounded-3xl bg-white border border-slate-100 p-5" data-testid="ws-detail">
          <h3 className="font-heading font-bold text-navy text-sm">{detail.workspace.name} — room management</h3>
          <div className="mt-3 grid lg:grid-cols-2 gap-4">
            <div>
              <h4 className="font-heading font-semibold text-navy text-xs">Members (each gets a private, logged portal link)</h4>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <input placeholder="Name" value={mbr.name} onChange={(e) => setMbr({ ...mbr, name: e.target.value })} data-testid="mbr-name" className={`${inputCls} w-32`} />
                <input placeholder="Email" value={mbr.email} onChange={(e) => setMbr({ ...mbr, email: e.target.value })} data-testid="mbr-email" className={`${inputCls} w-44`} />
                <select value={mbr.role} onChange={(e) => setMbr({ ...mbr, role: e.target.value })} data-testid="mbr-role" className={inputCls}>
                  {["workspace_admin", "reviewer", "viewer"].map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <button onClick={() => addMember(detail.workspace.workspace_id)} data-testid="mbr-add" className={btnNavy}>Add</button>
              </div>
              <div className="mt-2 space-y-1">
                {(detail.workspace.members || []).map((m) => (
                  <div key={m.member_id} className="flex justify-between items-center rounded-lg bg-slate-50 px-3 py-1.5 text-[11px] text-slate-500">
                    <span>{m.name} · {m.role} · NDA: {m.nda?.accepted ? "✓" : "pending"}</span>
                    <button onClick={() => copy(`${window.location.origin}${m.portal_path}`)} data-testid={`mbr-copy-${m.member_id}`} className="text-cobalt font-bold flex items-center gap-1"><Copy className="w-3 h-3" /> link</button>
                  </div>
                ))}
              </div>
              <h4 className="font-heading font-semibold text-navy text-xs mt-4">Shared documents (approved versions only)</h4>
              <div className="mt-2 flex gap-1.5">
                <select value={shareDoc} onChange={(e) => setShareDoc(e.target.value)} data-testid="ws-sharedoc" className={`${inputCls} flex-1`}>
                  <option value="">Select document…</option>
                  {docs.map((dd) => <option key={dd.doc_id} value={dd.doc_id}>{dd.title}</option>)}
                </select>
                <button onClick={() => share(detail.workspace.workspace_id)} data-testid="ws-share" className={btnNavy}>Share</button>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {(detail.workspace.doc_ids || []).map((id) => <span key={id} className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-500 text-[10px] font-semibold">{docs.find((x) => x.doc_id === id)?.title || id}</span>)}
              </div>
            </div>
            <div>
              <h4 className="font-heading font-semibold text-navy text-xs">Questions ({detail.questions.length})</h4>
              <div className="mt-2 space-y-1.5 max-h-56 overflow-y-auto">
                {detail.questions.map((q) => (
                  <div key={q.question_id} className="rounded-xl border border-slate-100 px-3 py-2 text-[11px]" data-testid={`ddq-${q.question_id}`}>
                    <b className="text-navy">{q.asked_by}:</b> <span className="text-slate-600">{q.text}</span>
                    {q.status === "answered" ? <div className="text-emerald-600 mt-1">✓ {q.answer}</div> : (
                      <div className="mt-1 flex gap-1.5">
                        <button onClick={() => answer(q.question_id, false)} data-testid={`ddq-answer-${q.question_id}`} className="px-2.5 py-1 rounded-full bg-navy text-white text-[10px] font-bold">Answer</button>
                        <button onClick={() => answer(q.question_id, true)} data-testid={`ddq-ai-${q.question_id}`} className="px-2.5 py-1 rounded-full bg-cobalt text-white text-[10px] font-bold flex items-center gap-1"><Sparkles className="w-3 h-3" /> AI draft</button>
                      </div>
                    )}
                  </div>
                ))}
                {!detail.questions.length && <p className="text-[11px] text-slate-300">No questions yet.</p>}
              </div>
              <h4 className="font-heading font-semibold text-navy text-xs mt-4">Access activity ({detail.activity.length})</h4>
              <div className="mt-2 space-y-1 max-h-36 overflow-y-auto">
                {detail.activity.map((a) => <div key={a.log_id} className="rounded-lg bg-slate-50 px-3 py-1.5 text-[10px] text-slate-400">{a.at.slice(0, 16).replace("T", " ")} · {a.grantee} · {a.action} · {a.result} · {a.ip}</div>)}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const DocPortalPanel = () => {
  const [docs, setDocs] = useState(null);
  const [form, setForm] = useState({ title: "", doc_type: "security_whitepaper", classification: "Confidential", content: "" });
  const fileRef = useRef();
  const [uploadDoc, setUploadDoc] = useState("");
  const load = useCallback(() => api.get("/enterprise-docs").then(({ data }) => setDocs(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!docs) return <Spinner />;
  const create = async () => {
    if (!form.title) { toast.error("Title required"); return; }
    try { await api.post("/enterprise-docs", { ...form, nda_required: true }); toast.success("Document created (v1 pending approval)"); setForm({ ...form, title: "", content: "" }); load(); }
    catch (e) { toast.error(err(e)); }
  };
  const approve = async (id, v) => {
    try { await api.post(`/enterprise-docs/${id}/versions/${v}/approve`); toast.success(`v${v} approved for external access`); load(); }
    catch (e) { toast.error(err(e)); }
  };
  const upload = async (file) => {
    if (!uploadDoc || !file) { toast.error("Select a document first"); return; }
    const fd = new FormData();
    fd.append("file", file);
    try { await api.post(`/enterprise-docs/${uploadDoc}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("File stored in object storage (new version, pending approval)"); load(); }
    catch (e) { toast.error(err(e)); }
  };
  return (
    <div className="space-y-4" data-testid="suite-docs">
      <div className="rounded-3xl bg-white border border-slate-100 p-5">
        <h3 className="font-heading font-bold text-navy text-sm flex items-center gap-2"><FileStack className="w-4 h-4 text-cobalt" /> Enterprise Document Portal — versioned, classified, watermark-ready</h3>
        <div className="mt-3 flex flex-wrap gap-2">
          <input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="doc-title" className={inputCls} />
          <select value={form.doc_type} onChange={(e) => setForm({ ...form, doc_type: e.target.value })} data-testid="doc-type" className={inputCls}>
            {["security_whitepaper", "compliance_report", "certification", "soc_report", "pen_test_summary", "policy_document", "questionnaire_response", "architecture_diagram", "bcdr_documentation", "vendor_documentation", "board_report", "due_diligence_package", "other"].map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
          </select>
          <select value={form.classification} onChange={(e) => setForm({ ...form, classification: e.target.value })} data-testid="doc-class" className={inputCls}>
            {["Public", "Internal", "Confidential", "Restricted"].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <button onClick={create} data-testid="doc-create" className={btnNavy}>Create</button>
        </div>
        <textarea placeholder="Content (or upload a file below after creating)" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })}
          data-testid="doc-content" className={`${inputCls} w-full mt-2 h-20`} />
        <div className="mt-2 flex flex-wrap gap-2 items-center">
          <select value={uploadDoc} onChange={(e) => setUploadDoc(e.target.value)} data-testid="doc-upload-target" className={inputCls}>
            <option value="">Upload file to…</option>
            {docs.map((d) => <option key={d.doc_id} value={d.doc_id}>{d.title}</option>)}
          </select>
          <input type="file" ref={fileRef} onChange={(e) => upload(e.target.files[0])} data-testid="doc-file" className="text-xs" />
          <span className="text-[10px] text-slate-400 flex items-center gap-1"><Upload className="w-3 h-3" /> stored in object storage · 25MB max</span>
        </div>
      </div>
      <div className="rounded-3xl bg-white border border-slate-100 p-5">
        <h4 className="font-heading font-semibold text-navy text-xs">Documents ({docs.length})</h4>
        <div className="mt-2 space-y-1.5 max-h-[420px] overflow-y-auto">
          {docs.map((d) => {
            const cur = (d.versions || []).find((v) => v.version === d.current_version) || {};
            const approved = cur.approval?.status === "approved";
            return (
              <div key={d.doc_id} className="flex flex-wrap justify-between items-center gap-2 rounded-xl border border-slate-100 px-3 py-2 text-xs" data-testid={`doc-row-${d.doc_id}`}>
                <span className="text-slate-600"><b className="text-navy">{d.title}</b> · {d.doc_type.replace(/_/g, " ")} · {d.classification} · v{d.current_version}{cur.storage_path ? " · file" : ""}</span>
                <span className="flex gap-1.5 items-center">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${approved ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>{approved ? "approved" : "pending approval"}</span>
                  {!approved && <button onClick={() => approve(d.doc_id, d.current_version)} data-testid={`doc-approve-${d.doc_id}`} className="px-2.5 py-1 rounded-full bg-navy text-white text-[10px] font-bold">Approve v{d.current_version}</button>}
                </span>
              </div>
            );
          })}
          {!docs.length && <p className="text-[11px] text-slate-300">No documents yet.</p>}
        </div>
      </div>
    </div>
  );
};

export const AiAssistantPanel = () => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [runs, setRuns] = useState([]);
  useEffect(() => { api.get("/suite/ai-questionnaire/runs").then(({ data }) => setRuns(data)).catch(() => {}); }, [result]);
  const run = async () => {
    const questions = text.split("\n").map((q) => q.trim()).filter(Boolean);
    if (!questions.length) { toast.error("Paste at least one question (one per line)"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/suite/ai-questionnaire", { questions });
      setResult(data); toast.success("Drafts generated — human approval required");
    } catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };
  const CONF = { high: "text-emerald-600", medium: "text-amber-500", low: "text-red-500" };
  return (
    <div className="space-y-4" data-testid="suite-ai">
      <div className="rounded-3xl bg-white border border-slate-100 p-5">
        <h3 className="font-heading font-bold text-navy text-sm flex items-center gap-2"><Sparkles className="w-4 h-4 text-cobalt" /> AI Security Questionnaire Assistant</h3>
        <p className="text-[11px] text-slate-400 mt-1">Paste customer questionnaire questions (one per line, max 25). Answers are drafted from LIVE control evidence only — the AI is hard-blocked from claiming certifications you don't hold. Every draft requires human review.</p>
        <textarea value={text} onChange={(e) => setText(e.target.value)} data-testid="ai-questions"
          placeholder={"Do you encrypt data at rest?\nDo you have SOC 2 certification?\nHow do you handle breach notification?"}
          className={`${inputCls} w-full mt-3 h-28`} />
        <button onClick={run} disabled={busy} data-testid="ai-run" className={`${btnNavy} mt-2 disabled:opacity-50`}>
          {busy ? "Drafting from live evidence…" : "Generate draft answers"}
        </button>
      </div>
      {result?.answers && (
        <div className="rounded-3xl bg-white border border-slate-100 p-5" data-testid="ai-results">
          <h4 className="font-heading font-semibold text-navy text-xs">Drafts ({result.answers.length}) — {result.note}</h4>
          <div className="mt-2 space-y-1.5 max-h-[420px] overflow-y-auto">
            {result.answers.map((a, i) => (
              <div key={i} className="rounded-xl border border-slate-100 px-3 py-2 text-xs" data-testid={`ai-answer-${i}`}>
                <b className="text-navy">{a.question}</b>
                <p className="text-slate-600 mt-1">{a.draft_answer}</p>
                <div className="text-[10px] text-slate-400 mt-1">controls: {(a.control_refs || []).join(", ") || "—"} · <span className={CONF[a.confidence]}>confidence: {a.confidence}</span>{a.gaps ? ` · gaps: ${a.gaps}` : ""}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="rounded-3xl bg-white border border-slate-100 p-5">
        <h4 className="font-heading font-semibold text-navy text-xs">Past runs ({runs.length})</h4>
        <div className="mt-2 space-y-1">
          {runs.map((r) => <div key={r.run_id} className="rounded-lg bg-slate-50 px-3 py-1.5 text-[11px] text-slate-500">{r.at.slice(0, 16).replace("T", " ")} · {r.status} · by {r.requested_by}</div>)}
        </div>
      </div>
    </div>
  );
};
